import { useCallback, useMemo, useState } from 'react';
import { useAccountsStore } from '@/entities/account';
import type { ExchangeAsset } from './types';
import {
  useAssetPrices,
  useAssetsIcons,
  useAssetsSymbolsDictionary,
} from '@/shared/api';
import { getAssetsFromExchangePairs } from '../lib';
import { useAppSettingsStore } from '@/shared/lib';
import { exchangeAssetsComparator } from '../lib/exchangeAssetsComparator';
import type { AssetSymbol } from '@/shared/types';

const useExchangeAssets = () => {
  const ewallet = useAccountsStore(state => state.ewallet);
  const { baseAsset } = useAppSettingsStore();

  const [selectedAssetToPay, setSelectedAssetToPay] = useState<
    ExchangeAsset | undefined
  >(undefined);
  const [selectedAssetToReceive, setSelectedAssetToReceive] = useState<
    ExchangeAsset | undefined
  >(undefined);

  const { assetsIconsData, isAssetsIconsLoading } = useAssetsIcons();
  const { data: assetsPricesData, isLoading: isAssetPricesLoading } =
    useAssetPrices({
      asset: baseAsset,
    });
  const { assetsSymbolsData, isAssetsSymbolsLoading } =
    useAssetsSymbolsDictionary();

  const loading =
    isAssetsIconsLoading || isAssetPricesLoading || isAssetsSymbolsLoading;

  const assetsFromSymbols = useMemo(() => {
    if (!assetsSymbolsData) return [];
    return getAssetsFromExchangePairs(assetsSymbolsData.value);
  }, [assetsSymbolsData]);

  const ewalletAvailableAssets = useMemo(() => {
    if (!ewallet) return [];

    const allAssets = ewallet.accounts.flatMap(acc => acc.assets);
    return allAssets.filter(a => a.available > 0);
  }, [ewallet]);

  const mergedAssets = useMemo(() => {
    const map = new Map();

    for (const a of assetsFromSymbols) map.set(a.code, a);
    for (const a of ewalletAvailableAssets) map.set(a.code, a);

    return [...map.values()];
  }, [assetsFromSymbols, ewalletAvailableAssets]);

  const getDeltaPercent = (asset: AssetSymbol) => {
    const assetPrice = assetsPricesData?.assets.find(
      assetPrice => assetPrice.code === asset.code
    );
    return {
      price: assetPrice?.baseRate ?? 0,
      deltaPercent: assetPrice?.percentChange24h ?? 0,
    };
  };

  const fullList = useMemo(() => {
    if (!assetsIconsData || !assetsPricesData) return [];

    return mergedAssets
      .map(asset => ({
        ...asset,
        icon: assetsIconsData.value[asset.code],
        value: asset.code,
        ...getDeltaPercent(asset),
      }))
      .sort(exchangeAssetsComparator);
  }, [mergedAssets, assetsIconsData, assetsPricesData]);

  const getAllowedCodes = (asset?: ExchangeAsset) => {
    if (!asset || !assetsSymbolsData) return null;

    const validPairs = assetsSymbolsData.value.filter(
      s => s.asset1.code === asset.code || s.asset2.code === asset.code
    );

    const set = new Set(
      validPairs.flatMap(p => [p.asset1.code, p.asset2.code])
    );

    set.delete(asset.code);

    return set;
  };

  const assetsExchangeFrom = useMemo(() => {
    const allowed = getAllowedCodes(selectedAssetToReceive);
    if (!allowed) return fullList;
    return fullList.filter(a => allowed.has(a.code));
  }, [selectedAssetToReceive, fullList]);

  const assetsExchangeTo = useMemo(() => {
    const allowed = getAllowedCodes(selectedAssetToPay);
    if (!allowed) return fullList;
    return fullList.filter(a => allowed.has(a.code));
  }, [selectedAssetToPay, fullList]);

  const resetSelectedAssets = useCallback(() => {
    setSelectedAssetToPay(undefined);
    setSelectedAssetToReceive(undefined);
  }, []);

  return {
    assetsExchangeFrom,
    assetsExchangeTo,
    selectedAssetToPay,
    setSelectedAssetToPay,
    selectedAssetToReceive,
    setSelectedAssetToReceive,
    resetSelectedAssets,
    isExchangeAssetsLoading: loading,
  };
};

export { useExchangeAssets };
